<template>
  <v-container fluid class="my-kesher-report pa-0">
    <!-- Login/Validation Section -->
    <v-layout v-if="!isAuthenticated" justify-center align-center fill-height>
      <v-card class="login-card elevation-12" max-width="480">
        <!-- Header with gradient -->
        <div class="login-header">
          <div class="header-pattern"></div>
          <v-icon size="64" color="white" class="header-icon"
            >mdi-phone-check</v-icon
          >
          <h1 class="white--text text-center display-1 font-weight-bold mt-3">
            My Kesher Report
          </h1>
          <p class="white--text text-center subtitle-1 mt-2 mb-0 px-4">
            Enter your credentials to access your report
          </p>
        </div>

        <v-card-text class="pt-6 px-6">
          <v-form ref="loginForm" v-model="validForm">
            <v-text-field
              v-model="credentials.phoneOrEmployeeId"
              label="Alert Phone# or Employee ID#"
              prepend-icon="mdi-account-circle"
              :rules="[rules.required]"
              outlined
              dense
              class="mb-3"
              color="teal darken-1"
              hint="Enter your 10-digit phone number or Employee ID"
              persistent-hint
            ></v-text-field>

            <v-text-field
              v-model="credentials.pin"
              label="PIN#"
              prepend-icon="mdi-lock"
              :rules="[rules.required, rules.pinNumber]"
              outlined
              dense
              class="mb-2"
              color="teal darken-1"
              :type="showPin ? 'text' : 'password'"
              :append-icon="showPin ? 'mdi-eye-off' : 'mdi-eye'"
              @click:append="showPin = !showPin"
              hint="Enter your 5-digit PIN"
              persistent-hint
              @keyup.enter="validateCredentials"
            ></v-text-field>

            <v-alert
              v-if="loginError"
              type="error"
              dense
              outlined
              class="mt-3"
              dismissible
              @input="loginError = ''"
            >
              {{ loginError }}
            </v-alert>
          </v-form>
        </v-card-text>

        <v-card-actions class="px-6 pb-6">
          <v-btn
            block
            large
            color="teal darken-1"
            dark
            :loading="validating"
            @click="validateCredentials"
            class="login-btn"
          >
            <v-icon left>mdi-login</v-icon>
            Sign In
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-layout>

    <!-- Authenticated Dashboard Section -->
    <v-layout v-else wrap class="dashboard-container pa-4">
      <!-- User Info Header -->
      <v-flex xs12>
        <v-card class="user-header-card mb-4" elevation="8">
          <div class="user-header-gradient">
            <v-layout row align-center justify-space-between class="pa-4">
              <v-layout align-center>
                <v-avatar size="70" color="white" class="mr-4 elevation-4">
                  <v-icon size="40" color="teal darken-1">mdi-account</v-icon>
                </v-avatar>
                <div>
                  <h2 class="white--text display-1 font-weight-bold mb-1">
                    {{ employee.teacherH || employee.teacher }}
                  </h2>
                  <p class="white--text subtitle-1 mb-0 opacity-80">
                    <v-icon small color="white" class="mr-1"
                      >mdi-badge-account</v-icon
                    >
                    Employee ID: {{ employee.employeeID }}
                  </p>
                  <p
                    class="white--text subtitle-2 mb-0 opacity-80"
                    v-if="employee.branch"
                  >
                    <v-icon small color="white" class="mr-1"
                      >mdi-office-building</v-icon
                    >
                    Branch: {{ employee.branch }}
                  </p>
                </div>
              </v-layout>
              <v-btn outline dark @click="logout" class="logout-btn">
                <v-icon left>mdi-logout</v-icon>
                Sign Out
              </v-btn>
            </v-layout>
          </div>
        </v-card>
      </v-flex>

      <!-- Action Cards -->
      <v-flex xs12 md6 class="pa-2">
        <v-card
          class="action-card report-card"
          hover
          @click="openReportDialog"
          height="220"
        >
          <div class="action-card-content">
            <div class="action-icon-wrapper report-icon">
              <v-icon size="56" color="white">mdi-file-document-outline</v-icon>
            </div>
            <h3 class="action-title mt-4">See / Print Report</h3>
            <p class="action-description">
              View your Kesher report and print a copy for your records
            </p>
            <v-btn outline color="indigo darken-1" class="mt-3">
              <v-icon left small>mdi-eye</v-icon>
              View Report
            </v-btn>
          </div>
        </v-card>
      </v-flex>

      <v-flex xs12 md6 class="pa-2">
        <v-card
          class="action-card settings-card"
          hover
          @click="openSettingsDialog"
          height="220"
        >
          <div class="action-card-content">
            <div class="action-icon-wrapper settings-icon">
              <v-icon size="56" color="white">mdi-cog-outline</v-icon>
            </div>
            <h3 class="action-title mt-4">Change Settings</h3>
            <p class="action-description">
              Update your Phone#, Alert Time & PIN#
            </p>
            <v-btn outline color="deep-orange darken-1" class="mt-3">
              <v-icon left small>mdi-pencil</v-icon>
              Edit Settings
            </v-btn>
          </div>
        </v-card>
      </v-flex>

      <!-- Quick Info Cards -->
      <v-flex xs12 class="mt-4">
        <v-layout row wrap>
          <v-flex xs12 sm4 class="pa-2">
            <v-card class="info-card" elevation="4">
              <v-card-text class="text-center">
                <v-icon size="36" color="teal darken-1">mdi-phone</v-icon>
                <p class="info-label mb-1 mt-2">Alert Phone</p>
                <p class="info-value mb-0">
                  {{ formatPhone(employee.alertPhone) || "Not Set" }}
                </p>
              </v-card-text>
            </v-card>
          </v-flex>
          <v-flex xs12 sm4 class="pa-2">
            <v-card class="info-card" elevation="4">
              <v-card-text class="text-center">
                <v-icon size="36" color="amber darken-2"
                  >mdi-clock-outline</v-icon
                >
                <p class="info-label mb-1 mt-2">Alert Time</p>
                <p class="info-value mb-0">
                  {{ employee.alertTime || "Not Set" }}
                </p>
              </v-card-text>
            </v-card>
          </v-flex>
          <v-flex xs12 sm4 class="pa-2">
            <v-card class="info-card" elevation="4">
              <v-card-text class="text-center">
                <v-icon size="36" color="deep-purple">mdi-lock-outline</v-icon>
                <p class="info-label mb-1 mt-2">PIN Status</p>
                <p class="info-value mb-0">
                  <v-chip
                    small
                    :color="employee.pinNumber ? 'success' : 'error'"
                    dark
                  >
                    {{ employee.pinNumber ? "Set" : "Not Set" }}
                  </v-chip>
                </p>
              </v-card-text>
            </v-card>
          </v-flex>
        </v-layout>
      </v-flex>
    </v-layout>

    <!-- Report Dialog -->
    <v-dialog v-model="reportDialog" max-width="900" persistent>
      <v-card class="report-dialog">
        <v-toolbar flat color="indigo darken-1" dark>
          <v-toolbar-title>
            <v-icon class="mr-2">mdi-file-document</v-icon>
            My Kesher Report
          </v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon @click="reportDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>
        <v-card-text class="pa-4">
          <v-layout row justify-end class="mb-4">
            <v-btn color="primary" outline @click="printReport">
              <v-icon left>mdi-printer</v-icon>
              Print Report
            </v-btn>
          </v-layout>

          <div ref="reportContent" class="report-content">
            <div class="report-header text-center mb-4">
              <h2 class="headline font-weight-bold">Kesher Employee Report</h2>
              <p class="grey--text">Generated on {{ currentDate }}</p>
            </div>

            <v-divider class="mb-4"></v-divider>

            <v-layout row wrap>
              <v-flex xs6 class="pa-2">
                <p class="mb-2">
                  <strong>Name:</strong> {{ employee.teacher }}
                </p>
                <p class="mb-2">
                  <strong>Hebrew Name:</strong> {{ employee.teacherH }}
                </p>
                <p class="mb-2">
                  <strong>Employee ID:</strong> {{ employee.employeeID }}
                </p>
              </v-flex>
              <v-flex xs6 class="pa-2">
                <p class="mb-2">
                  <strong>Branch:</strong> {{ employee.branch }}
                </p>
                <p class="mb-2"><strong>Class:</strong> {{ employee.class }}</p>
                <p class="mb-2">
                  <strong>Section:</strong> {{ employee.section }}
                </p>
              </v-flex>
            </v-layout>

            <v-divider class="my-4"></v-divider>

            <h3 class="title mb-3">Contact Information</h3>
            <v-layout row wrap>
              <v-flex xs6 class="pa-2">
                <p class="mb-2">
                  <strong>Alert Phone:</strong>
                  {{ formatPhone(employee.alertPhone) }}
                </p>
                <p class="mb-2">
                  <strong>Alert Time:</strong> {{ employee.alertTime }}
                </p>
              </v-flex>
              <v-flex xs6 class="pa-2">
                <p class="mb-2">
                  <strong>SMS Notification:</strong>
                  {{ employee.smsNotification ? "Enabled" : "Disabled" }}
                </p>
                <p class="mb-2">
                  <strong>Connection Mode:</strong>
                  {{ employee.connectionMode }}
                </p>
              </v-flex>
            </v-layout>

            <!-- Placeholder for more report data -->
            <v-alert type="info" outline class="mt-4">
              <v-icon slot="prepend">mdi-information</v-icon>
              Additional report details will be displayed here once the API is
              connected.
            </v-alert>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Settings Dialog -->
    <v-dialog v-model="settingsDialog" max-width="500" persistent>
      <v-card class="settings-dialog">
        <v-toolbar flat color="deep-orange darken-1" dark>
          <v-toolbar-title>
            <v-icon class="mr-2">mdi-cog</v-icon>
            Update Settings
          </v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon @click="settingsDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>
        <v-card-text class="pa-5">
          <v-form ref="settingsForm" v-model="validSettings">
            <v-text-field
              v-model="settings.alertPhone"
              label="Alert Phone#"
              prepend-icon="mdi-phone"
              :rules="[rules.phoneNumber]"
              outlined
              dense
              class="mb-3"
              color="deep-orange darken-1"
              hint="10 or 11 digit phone number"
              persistent-hint
            ></v-text-field>

            <v-menu
              ref="alertTimeMenu"
              v-model="timePickerMenu"
              :close-on-content-click="false"
              :return-value.sync="settings.alertTime"
              transition="scale-transition"
              offset-y
            >
              <template v-slot:activator="{ on }">
                <v-text-field
                  v-model="settings.alertTime"
                  label="Alert Time"
                  prepend-icon="mdi-clock-outline"
                  readonly
                  outlined
                  dense
                  class="mb-3"
                  color="deep-orange darken-1"
                  v-on="on"
                ></v-text-field>
              </template>
              <v-time-picker
                v-model="settings.alertTime"
                @input="$refs.alertTimeMenu.save(settings.alertTime)"
                color="deep-orange darken-1"
              ></v-time-picker>
            </v-menu>

            <v-text-field
              v-model="settings.currentPin"
              label="Current PIN#"
              prepend-icon="mdi-lock"
              :rules="[rules.required, rules.pinNumber]"
              outlined
              dense
              class="mb-3"
              color="deep-orange darken-1"
              type="password"
            ></v-text-field>

            <v-text-field
              v-model="settings.newPin"
              label="New PIN# (leave blank to keep current)"
              prepend-icon="mdi-lock-reset"
              :rules="settings.newPin ? [rules.pinNumber] : []"
              outlined
              dense
              class="mb-3"
              color="deep-orange darken-1"
              type="password"
              hint="5 digits required for new PIN"
              persistent-hint
            ></v-text-field>

            <v-text-field
              v-if="settings.newPin"
              v-model="settings.confirmPin"
              label="Confirm New PIN#"
              prepend-icon="mdi-lock-check"
              :rules="[rules.pinMatch]"
              outlined
              dense
              class="mb-3"
              color="deep-orange darken-1"
              type="password"
            ></v-text-field>

            <v-alert
              v-if="settingsError"
              type="error"
              dense
              outlined
              class="mt-3"
              dismissible
              @input="settingsError = ''"
            >
              {{ settingsError }}
            </v-alert>

            <v-alert
              v-if="settingsSuccess"
              type="success"
              dense
              outlined
              class="mt-3"
              dismissible
              @input="settingsSuccess = ''"
            >
              {{ settingsSuccess }}
            </v-alert>
          </v-form>
        </v-card-text>
        <v-card-actions class="px-5 pb-5">
          <v-spacer></v-spacer>
          <v-btn flat @click="settingsDialog = false">Cancel</v-btn>
          <v-btn
            color="deep-orange darken-1"
            dark
            :loading="savingSettings"
            @click="saveSettings"
          >
            <v-icon left>mdi-content-save</v-icon>
            Save Changes
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import moment from "moment";

export default {
  name: "MyKesherReport",
  data: () => ({
    isAuthenticated: false,
    validating: false,
    showPin: false,
    loginError: "",
    validForm: false,

    credentials: {
      phoneOrEmployeeId: "",
      pin: "",
    },

    employee: {
      employeeID: "",
      teacher: "",
      teacherH: "",
      alertPhone: "",
      alertTime: "",
      pinNumber: "",
      branch: "",
      class: "",
      section: "",
      building: "",
      connectionMode: "",
      smsNotification: false,
    },

    // Dialogs
    reportDialog: false,
    settingsDialog: false,
    timePickerMenu: false,

    // Settings form
    validSettings: false,
    savingSettings: false,
    settingsError: "",
    settingsSuccess: "",
    settings: {
      alertPhone: "",
      alertTime: "",
      currentPin: "",
      newPin: "",
      confirmPin: "",
    },

    rules: {
      required: (v) => !!v || "This field is required",
      pinNumber: (v) => {
        if (!v) return true;
        const cleaned = v.replace(/-/g, "");
        return (
          (cleaned.length === 5 && /^\d+$/.test(cleaned)) ||
          "PIN must be 5 digits"
        );
      },
      phoneNumber: (v) => {
        if (!v) return true;
        const cleaned = v.replace(/-/g, "");
        return (
          ([10, 11].includes(cleaned.length) && /^\d+$/.test(cleaned)) ||
          "Invalid phone number"
        );
      },
    },
  }),

  computed: {
    currentDate() {
      return moment().format("MMMM D, YYYY");
    },
  },

  created() {
    // Add pinMatch rule that references settings
    this.rules.pinMatch = (v) => {
      return v === this.settings.newPin || "PINs do not match";
    };
  },

  methods: {
    async validateCredentials() {
      if (!this.$refs.loginForm.validate()) return;

      this.validating = true;
      this.loginError = "";

      try {
        // TODO: Replace with actual API call
        const response = await this.$http.post(
          "api/kesherEmployees/getEmployee",
          {
            phoneOrEmployeeId: this.credentials.phoneOrEmployeeId,
            pin: this.credentials.pin,
          }
        );

        if (response && response.employeeID) {
          this.employee = response;
          this.isAuthenticated = true;

          // Pre-fill settings
          this.settings.alertPhone = this.employee.alertPhone || "";
          this.settings.alertTime = this.employee.alertTime
            ? moment(this.employee.alertTime, "hh:mm A").format("HH:mm")
            : "";
        } else {
          this.loginError = "Invalid credentials. Please try again.";
        }
      } catch (error) {
        this.loginError =
          (error.response &&
            error.response.data &&
            error.response.data.message) ||
          "Unable to validate credentials. Please try again.";
      } finally {
        this.validating = false;
      }
    },

    logout() {
      this.isAuthenticated = false;
      this.employee = {};
      this.credentials = { phoneOrEmployeeId: "", pin: "" };
      this.settings = {
        alertPhone: "",
        alertTime: "",
        currentPin: "",
        newPin: "",
        confirmPin: "",
      };
    },

    openReportDialog() {
      this.reportDialog = true;
    },

    openSettingsDialog() {
      this.settingsError = "";
      this.settingsSuccess = "";
      this.settings.currentPin = "";
      this.settings.newPin = "";
      this.settings.confirmPin = "";
      this.settingsDialog = true;
    },

    formatPhone(phone) {
      if (!phone) return "";
      const cleaned = phone.replace(/\D/g, "");
      if (cleaned.length === 11) {
        return `${cleaned.slice(0, 1)}-${cleaned.slice(1, 4)}-${cleaned.slice(
          4,
          7
        )}-${cleaned.slice(7)}`;
      } else if (cleaned.length === 10) {
        return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 6)}-${cleaned.slice(
          6
        )}`;
      }
      return phone;
    },

    async saveSettings() {
      if (!this.$refs.settingsForm.validate()) return;

      // Validate current PIN matches
      if (this.settings.currentPin !== this.credentials.pin) {
        this.settingsError = "Current PIN is incorrect";
        return;
      }

      this.savingSettings = true;
      this.settingsError = "";
      this.settingsSuccess = "";

      try {
        // TODO: Replace with actual API call
        await this.$http.post("api/kesherEmployees/updateSettings", {
          employeeID: this.employee.employeeID,
          alertPhone: this.settings.alertPhone,
          alertTime: this.settings.alertTime
            ? moment(this.settings.alertTime, "HH:mm").format("hh:mm A")
            : null,
          newPin: this.settings.newPin || null,
        });

        // Update local employee data
        this.employee.alertPhone = this.settings.alertPhone;
        this.employee.alertTime = this.settings.alertTime
          ? moment(this.settings.alertTime, "HH:mm").format("hh:mm A")
          : this.employee.alertTime;

        if (this.settings.newPin) {
          this.credentials.pin = this.settings.newPin;
          this.employee.pinNumber = this.settings.newPin;
        }

        this.settingsSuccess = "Settings updated successfully!";

        setTimeout(() => {
          this.settingsDialog = false;
        }, 1500);
      } catch (error) {
        this.settingsError =
          (error.response &&
            error.response.data &&
            error.response.data.message) ||
          "Unable to save settings. Please try again.";
      } finally {
        this.savingSettings = false;
      }
    },

    printReport() {
      const printContent = this.$refs.reportContent.innerHTML;
      const printWindow = window.open("", "_blank");
      printWindow.document.write(`
        <html>
          <head>
            <title>Kesher Report - ${this.employee.teacherH ||
              this.employee.teacher}</title>
            <style>
              body {
                font-family: Arial, sans-serif;
                padding: 40px;
                max-width: 800px;
                margin: 0 auto;
              }
              h2 { color: #1a237e; margin-bottom: 5px; }
              h3 { color: #333; margin-top: 20px; }
              p { margin: 8px 0; line-height: 1.6; }
              strong { color: #444; }
              .report-header { text-align: center; border-bottom: 2px solid #1a237e; padding-bottom: 20px; margin-bottom: 30px; }
              @media print {
                body { padding: 20px; }
              }
            </style>
          </head>
          <body>
            ${printContent}
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.print();
    },
  },
};
</script>

<style scoped>
.my-kesher-report {
  min-height: calc(100vh - 64px);
  background: linear-gradient(135deg, #e0f2f1 0%, #e8eaf6 50%, #fce4ec 100%);
}

/* Login Card Styles */
.login-card {
  border-radius: 16px !important;
  overflow: hidden;
}

.login-header {
  background: linear-gradient(135deg, #00897b 0%, #26a69a 50%, #4db6ac 100%);
  padding: 40px 30px;
  position: relative;
  overflow: hidden;
}

.header-pattern {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: radial-gradient(
      circle at 20% 80%,
      rgba(255, 255, 255, 0.1) 0%,
      transparent 50%
    ),
    radial-gradient(
      circle at 80% 20%,
      rgba(255, 255, 255, 0.15) 0%,
      transparent 50%
    );
}

.header-icon {
  position: relative;
  z-index: 1;
  display: block;
  margin: 0 auto;
  animation: pulse 2s ease-in-out infinite;
}

@keyframes pulse {
  0%,
  100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
}

.login-btn {
  border-radius: 8px;
  text-transform: none;
  font-size: 16px;
  letter-spacing: 0.5px;
}

/* Dashboard Styles */
.dashboard-container {
  max-width: 1200px;
  margin: 0 auto;
}

.user-header-card {
  border-radius: 16px !important;
  overflow: hidden;
}

.user-header-gradient {
  background: linear-gradient(135deg, #00897b 0%, #00695c 100%);
}

.opacity-80 {
  opacity: 0.85;
}

.logout-btn {
  border-color: rgba(255, 255, 255, 0.7) !important;
  text-transform: none;
}

/* Action Cards */
.action-card {
  border-radius: 16px !important;
  cursor: pointer;
  transition: all 0.3s ease;
  overflow: hidden;
}

.action-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15) !important;
}

.action-card-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 24px;
  text-align: center;
}

.action-icon-wrapper {
  width: 90px;
  height: 90px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.2);
}

.report-icon {
  background: linear-gradient(135deg, #3949ab 0%, #5c6bc0 100%);
}

.settings-icon {
  background: linear-gradient(135deg, #e64a19 0%, #ff7043 100%);
}

.action-title {
  font-size: 1.3rem;
  font-weight: 600;
  color: #333;
}

.action-description {
  color: #666;
  font-size: 0.95rem;
  margin-top: 8px;
}

/* Info Cards */
.info-card {
  border-radius: 12px !important;
  transition: transform 0.2s ease;
}

.info-card:hover {
  transform: translateY(-4px);
}

.info-label {
  font-size: 0.85rem;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.info-value {
  font-size: 1.1rem;
  font-weight: 600;
  color: #333;
}

/* Dialog Styles */
.report-dialog,
.settings-dialog {
  border-radius: 12px !important;
}

.report-content {
  padding: 20px;
  background: #fafafa;
  border-radius: 8px;
}
</style>
